import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseSuggestionsProps<T = any> {
  value?: any;
  searchEndpoint?: string;
  otherSearchEndpoint?: string;
  rows?: T[];
  columnDisplay?: keyof T;
  searchColumns?: any[];
  displayRelatedField?: keyof T;
  onRowSelection?: (row: T | null) => void;
  onValueChange?: (value: any) => void;
  fetchSearchResults: (endpoint?: string, otherEndpoint?: string, query?: string, options?: any) => void;
  searchResults: T[];
  setSearchResults: (results: T[]) => void;
  isLoading: boolean;
  handlePageChange?: (page: number, searchQuery?: string) => Promise<void>;
  currentPage?: number;
  totalItems?: number;
  pageSize?: number;
}

export const useSuggestions = <T = any>({
  value,
  searchEndpoint,
  otherSearchEndpoint,
  rows,
  columnDisplay,
  searchColumns,
  displayRelatedField,
  onRowSelection,
  onValueChange,
  fetchSearchResults,
  searchResults,
  setSearchResults,
  isLoading,
  handlePageChange,
  currentPage = 0,
  totalItems = 0,
  pageSize = 10
}: UseSuggestionsProps<T>) => {
  const [selectedObj, setSelectedObj] = useState<T | null>(null);
  const [localSelectedObj, setLocalSelectedObj] = useState<T | null>(null);
  const [inputValue, setInputValue] = useState<string>(() =>
    value !== undefined && value !== null ? String(value) : ''
  );
  const [debouncedValue, setDebouncedValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [localCurrentPage, setLocalCurrentPage] = useState<number>(0);
  const suggestionContainerRef = useRef<HTMLDivElement | null>(null);
  const suggestionDropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPositionStyle, setDropdownPositionStyle] = useState<CSSProperties>({});
  const isUserTypingRef = useRef<boolean>(false);
  const previousValueRef = useRef<any>(value);

  const shouldUseServer = Boolean(searchEndpoint || otherSearchEndpoint);
  const effectivePageSize = pageSize > 0 ? pageSize : 10;

  const hasDisplayConfig = Boolean(displayRelatedField || (columnDisplay && String(columnDisplay).trim() !== ''));

  const getFieldRawValue = (row: any, field?: string) => {
    if (!row || !field) return undefined;
    if (field.includes('.')) {
      return field.split('.').reduce<any>((acc, part) => (acc ? acc[part] : undefined), row);
    }
    return (row as any)?.[field];
  };

  const resolveFieldValue = useCallback((row: any, field?: string): string => {
    return getFieldRawValue(row, field);
  }, []);

  const primaryField = useMemo(() => {
    if (columnDisplay && typeof columnDisplay === 'string' && columnDisplay.length > 0) {
      return columnDisplay as string;
    }
    const fallbackColumn = searchColumns?.find(
      column => typeof column?.field === 'string' && column.field !== 'checkbox'
    );
    return fallbackColumn?.field as string | undefined;
  }, [columnDisplay, searchColumns]);

  const secondaryField = useMemo(() => {
    if (displayRelatedField && typeof displayRelatedField === 'string' && displayRelatedField.length > 0) {
      return displayRelatedField as string;
    }
    const fallbackColumn = searchColumns?.find(
      column => typeof column?.field === 'string' && column.field !== 'checkbox' && column.field !== primaryField
    );
    return fallbackColumn?.field as string | undefined;
  }, [displayRelatedField, primaryField, searchColumns]);

  const suggestionColumns = useMemo(() => {
    if (searchColumns && searchColumns.length > 0) {
      const visibleColumns = searchColumns.filter(column => {
        const field = column?.field;
        return typeof field !== 'string' || field !== 'checkbox';
      });

      if (visibleColumns.length > 0) {
        return visibleColumns.slice(0, 2);
      }
    }

    const fallbackColumns: any[] = [];
    if (primaryField) {
      fallbackColumns.push({ field: primaryField });
    }
    if (secondaryField && secondaryField !== primaryField) {
      fallbackColumns.push({ field: secondaryField });
    }

    return fallbackColumns;
  }, [searchColumns, primaryField, secondaryField]);

  const normalizedValueProp = value !== undefined && value !== null ? String(value) : '';

  useEffect(() => {
    if (isUserTypingRef.current) {
      return;
    }

    if (inputValue !== normalizedValueProp) {
      setInputValue(normalizedValueProp);

      if (selectedObj) {
        const currentDisplay = resolveFieldValue(selectedObj, primaryField);
        if (currentDisplay !== normalizedValueProp) {
          setSelectedObj(null);
          setLocalSelectedObj(null);
        }
      }
    }
  }, [normalizedValueProp, inputValue, selectedObj, localSelectedObj, primaryField, resolveFieldValue]);

  const getColumnDisplayValue = (row: any, column: any): string => {
    if (!column) return '';
    const baseField = typeof column.field === 'string' ? column.field : undefined;
    let rawValue = getFieldRawValue(row, baseField);

    if (typeof column.valueGetter === 'function') {
      try {
        rawValue = column.valueGetter({
          row,
          field: column.field,
          value: rawValue,
          id: (row as any)?.id ?? (row as any)?.uuid ?? rawValue,
          getValue: (fieldName: string) => getFieldRawValue(row, fieldName)
        });
      } catch (error) {
        // ignore valueGetter errors for preview
      }
    }

    if (typeof column.valueFormatter === 'function') {
      try {
        rawValue = column.valueFormatter({
          value: rawValue,
          row,
          field: column.field
        });
      } catch (error) {
        // ignore formatter errors for preview
      }
    }

    return rawValue;
  };

  const getColumnStyle = (column: any, index: number) => {
    if (!column) {
      return { flex: index === 0 ? '1 1 0%' : '1 1 0%' };
    }

    if (column.width) {
      return { width: `${column.width}px`, flex: '0 0 auto' };
    }

    if (column.flex) {
      const flexValue = typeof column.flex === 'number' ? column.flex : 1;
      return { flex: `${flexValue} ${flexValue} 0%` };
    }

    return { flex: index === 0 ? '1 1 0%' : '1 1 0%' };
  };

  const filteredLocalRows = useMemo(() => {
    if (!hasDisplayConfig) return [] as T[];
    if (!rows || rows.length === 0) return [] as T[];
    if (!debouncedValue) {
      return rows;
    }

    const searchTerm = debouncedValue.toLowerCase();

    return rows.filter(row =>
      Object.values(row ?? {}).some(value => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value).toLowerCase().includes(searchTerm);
          } catch (error) {
            return false;
          }
        }
        return String(value).toLowerCase().includes(searchTerm);
      })
    );
  }, [rows, debouncedValue, hasDisplayConfig]);

  const totalLocalItems = filteredLocalRows.length;

  const paginatedLocalRows = useMemo(() => {
    const start = localCurrentPage * effectivePageSize;
    return filteredLocalRows.slice(start, start + effectivePageSize);
  }, [filteredLocalRows, localCurrentPage, effectivePageSize]);

  const suggestions = useMemo(() => {
    return (shouldUseServer ? searchResults : paginatedLocalRows) ?? [];
  }, [paginatedLocalRows, searchResults, shouldUseServer]);

  const hasQuery = debouncedValue.length > 0;
  const hasSource = shouldUseServer || Boolean(rows?.length);
  const shouldShowDropdown =
    hasDisplayConfig && hasSource && isFocused && hasQuery && (isLoading || suggestions.length > 0 || shouldUseServer);
  const showNoResults = shouldShowDropdown && hasQuery && !isLoading && suggestions.length === 0;

  const clearSelection = () => {
    setSelectedObj(null);
    setLocalSelectedObj(null);
  };

  const handleSuggestionSelect = (row: T) => {
    isUserTypingRef.current = false;
    const displayValue = resolveFieldValue(row, primaryField);
    setInputValue(displayValue);
    setSelectedObj(row);
    setLocalSelectedObj(row);
    onRowSelection?.(row);
    setIsFocused(false);
    setHighlightedIndex(-1);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isArrowKey = event.key === 'ArrowDown' || event.key === 'ArrowUp';
    const hasInputValue = Boolean(inputValue?.trim());

    if (isArrowKey && hasInputValue) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!shouldShowDropdown) return;

    if (event.key === 'ArrowDown') {
      if (suggestions.length === 0) return;
      setHighlightedIndex(prev => {
        const nextIndex = prev < suggestions.length - 1 ? prev + 1 : 0;
        return nextIndex;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      setHighlightedIndex(prev => {
        if (prev <= 0) {
          return suggestions.length - 1;
        }
        return prev - 1;
      });
      return;
    }

    if (event.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        event.preventDefault();
        handleSuggestionSelect(suggestions[highlightedIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      setIsFocused(false);
    }
  };

  const handleInputChange = (nextValue: string) => {
    isUserTypingRef.current = true;
    setInputValue(nextValue);
    if (!isFocused) {
      setIsFocused(true);
    }
    if (selectedObj) {
      clearSelection();
    }
    if (localSelectedObj) {
      setLocalSelectedObj(null);
    }
    onValueChange?.(nextValue);
    if (!nextValue) {
      onRowSelection?.(null);
    }

    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 100);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setHasUserInteracted(true);
  };
  const handleInputBlur = () => {
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 200);

    if (!isLoading && suggestions.length > 0 && !selectedObj) {
      handleSuggestionSelect(suggestions[0]);
      return;
    }
    setIsFocused(false);
  };

  useEffect(() => {
    if (!shouldUseServer) {
      setLocalCurrentPage(0);
    }
  }, [debouncedValue, shouldUseServer]);

  useEffect(() => {
    if (shouldUseServer) return;

    const maxPage = Math.max(0, Math.ceil(totalLocalItems / effectivePageSize) - 1);
    setLocalCurrentPage(prev => (prev > maxPage ? maxPage : prev));
  }, [totalLocalItems, effectivePageSize, shouldUseServer]);

  useEffect(() => {
    if (!selectedObj) {
      if (!value) {
        setInputValue(prev => (prev === '' ? prev : ''));
      }
      return;
    }

    const displayValue = resolveFieldValue(selectedObj, primaryField);
    if (displayValue) {
      setInputValue(displayValue);
    }
  }, [selectedObj, primaryField, value, resolveFieldValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!shouldUseServer) return;
    if (!hasDisplayConfig) {
      setSearchResults([]);
      return;
    }

    if (!debouncedValue) {
      setSearchResults([]);
      return;
    }

    if (hasUserInteracted) {
      if (typeof handlePageChange === 'function') {
        handlePageChange(0, debouncedValue);
      } else {
        fetchSearchResults(searchEndpoint, otherSearchEndpoint, debouncedValue, {
          page: 0,
          pageSize: effectivePageSize
        });
      }
    }
  }, [
    debouncedValue,
    shouldUseServer,
    hasDisplayConfig,
    fetchSearchResults,
    searchEndpoint,
    otherSearchEndpoint,
    setSearchResults,
    hasUserInteracted,
    handlePageChange,
    effectivePageSize
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const containerEl = suggestionContainerRef.current;
      const dropdownEl = suggestionDropdownRef.current;

      if (containerEl?.contains(targetNode)) return;
      if (dropdownEl?.contains(targetNode)) return;

      setIsFocused(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!shouldShowDropdown) {
      setHighlightedIndex(-1);
      return;
    }

    if (suggestions.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [shouldShowDropdown, suggestions, debouncedValue]);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldShowDropdown) {
      setDropdownPositionStyle({});
      return;
    }

    const rect = suggestionContainerRef.current?.getBoundingClientRect();
    if (!rect) {
      setDropdownPositionStyle({});
      return;
    }

    const scrollY = window.scrollY ?? 0;
    const scrollX = window.scrollX ?? 0;
    const width = suggestionContainerRef.current?.offsetWidth ?? 'auto';
    const isLowerHalf = rect.top > window.innerHeight / 2;
    const baseStyle = {
      left: rect.left + scrollX,
      width
    };

    if (isLowerHalf) {
      setDropdownPositionStyle({
        ...baseStyle,
        top: rect.top + scrollY,
        transform: 'translateY(-100%)',
        marginBottom: 4
      });
      return;
    }

    setDropdownPositionStyle({
      ...baseStyle,
      top: rect.bottom + scrollY,
      marginTop: 4
    });
  }, [shouldShowDropdown, suggestions.length]);

  const effectiveCurrentPage = shouldUseServer ? currentPage : localCurrentPage;
  const totalAvailableItems = shouldUseServer ? totalItems : totalLocalItems;
  const canGoPrevious = effectiveCurrentPage > 0;
  const canGoNext = totalAvailableItems > (effectiveCurrentPage + 1) * effectivePageSize;
  const showPagination = totalAvailableItems > effectivePageSize;

  const goToPreviousPage = useCallback(() => {
    if (isLoading || !canGoPrevious) return;

    if (shouldUseServer) {
      const previousPage = Math.max(0, (currentPage ?? 0) - 1);
      if (typeof handlePageChange === 'function') {
        handlePageChange(previousPage, debouncedValue);
      }
    } else {
      setLocalCurrentPage(prev => Math.max(0, prev - 1));
    }
  }, [isLoading, canGoPrevious, shouldUseServer, currentPage, handlePageChange, debouncedValue]);

  const goToNextPage = useCallback(() => {
    if (isLoading || !canGoNext) return;

    if (shouldUseServer) {
      const nextPage = (currentPage ?? 0) + 1;
      if (typeof handlePageChange === 'function') {
        handlePageChange(nextPage, debouncedValue);
      }
    } else {
      setLocalCurrentPage(prev => prev + 1);
    }
  }, [isLoading, canGoNext, shouldUseServer, currentPage, handlePageChange, debouncedValue]);

  return {
    // State
    selectedObj,
    setSelectedObj,
    localSelectedObj,
    setLocalSelectedObj,
    inputValue,
    isFocused,
    highlightedIndex,
    setHighlightedIndex,
    suggestionContainerRef,
    suggestionDropdownRef,
    suggestions,
    shouldShowDropdown,
    showNoResults,

    // Computed values
    primaryField,
    secondaryField,
    suggestionColumns,

    // Methods
    clearSelection,
    handleSuggestionSelect,
    handleInputKeyDown,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    getColumnDisplayValue,
    getColumnStyle,
    resolveFieldValue,
    goToPreviousPage,
    goToNextPage,
    canGoPrevious,
    canGoNext,
    showPagination,
    dropdownPositionStyle
  };
};
